// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  Box,
  BreakdownIcon,
  HeatmapIcon,
  GlobeIcon,
  MetricIcon,
  NumberSignIcon,
  OrbitIcon,
  PerformanceIcon,
  Row,
  Select,
  TableIcon,
} from "@hightouch/htui";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { DASHBOARD_UNSUPPORTED_CHART_TYPES } from "./constants";
import type { ChartFormState } from "./types";
import { GraphType } from "@hightouch/lib/query/visual/types";

export const DecisionEngineFlowIcon = () => (
  <Row as={OrbitIcon} boxSize="20px" color="text.secondary" />
);

export const BaseGlobeIcon = () => (
  <Row as={GlobeIcon} boxSize="20px" color="text.secondary" />
);

export const GraphTypeIcons: Record<GraphType, JSX.Element> = {
  [GraphType.Line]: <MetricIcon />,
  [GraphType.Column]: (
    // Any additional icon wrapper needs to be in a centered flex box with 100% width and height to ensure alignment with the parent.
    <Row
      align="center"
      justify="center"
      boxSize="100%"
      transform="rotate(270deg)"
    >
      <BreakdownIcon />
    </Row>
  ),
  [GraphType.Area]: <PerformanceIcon />,
  [GraphType.Bar]: <BreakdownIcon />,
  [GraphType.Table]: <TableIcon />,
  // TODO(XXX): Add heatmap icon from new htui
  [GraphType.HeatMap]: <HeatmapIcon />,
  [GraphType.BigNumber]: <NumberSignIcon />,
};

export const GRAPH_OPTIONS = [
  {
    label: "Line",
    value: GraphType.Line,
    icon: (props) => <MetricIcon {...props} color="text.secondary" />,
  },
  {
    label: "Column",
    value: GraphType.Column,
    icon: (props) => (
      <Box transform="rotate(270deg)">
        <BreakdownIcon {...props} color="text.secondary" />
      </Box>
    ),
  },
  {
    label: "Area",
    value: GraphType.Area,
    icon: (props) => <PerformanceIcon {...props} color="text.secondary" />,
  },
  {
    label: "Bar",
    value: GraphType.Bar,
    icon: (props) => <BreakdownIcon {...props} color="text.secondary" />,
  },
  {
    label: "Table",
    value: GraphType.Table,
    icon: (props) => <TableIcon {...props} color="text.secondary" />,
  },
  {
    label: "Heat Map",
    value: GraphType.HeatMap,
    // TODO(XXX): Add heatmap icon from new htui
    icon: (props) => <HeatmapIcon {...props} color="text.secondary" />,
  },
  {
    label: "Number",
    value: GraphType.BigNumber,
    icon: (props) => <NumberSignIcon {...props} color="text.secondary" />,
  },
];

// Narrowed form type to avoid TS2615: react-hook-form's PathImpl triggers
// infinite type expansion on ChartFormState's recursive NestableCondition types.
type GraphOptionsFormState = Pick<
  ChartFormState,
  "graphType" | "addToDashboardOptions"
>;

export const GraphOptionsSelection = () => {
  const form = useFormContext<GraphOptionsFormState>();
  const addToDashboardOptions = form.watch("addToDashboardOptions");

  const options = useMemo(() => {
    if (addToDashboardOptions) {
      return GRAPH_OPTIONS.filter(
        (option) => !DASHBOARD_UNSUPPORTED_CHART_TYPES.includes(option.value),
      );
    }
    return GRAPH_OPTIONS;
  }, [addToDashboardOptions]);

  return (
    <Controller
      control={form.control}
      name="graphType"
      render={({ field }) => (
        <Box>
          <Select
            optionAccessories={({ icon }) => ({
              leftAccessory: {
                type: "icon",
                icon,
              },
            })}
            options={options}
            value={field.value}
            size="md"
            onChange={field.onChange}
            popoverWidth="4xs"
            width="100%"
          />
        </Box>
      )}
    />
  );
};
